import { useMemo, useState, ReactNode, useContext, createContext } from 'react';
import { Button, Dialog, DialogTitle, DialogActions, DialogContent } from '@mui/material';
import { useTranslate } from 'src/locales';

interface ConfirmDialogContextType {
  openDialog: (callback: () => void, options?: { message?: string; title?: string }) => void;
}

const ConfirmDialogContext = createContext<ConfirmDialogContextType | undefined>(undefined);

export function ConfirmDialogProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [onConfirm, setOnConfirm] = useState<(() => void) | null>(null);
  const [message, setMessage] = useState<string>('');
  const [title, setTitle] = useState<string>('');

  const openDialog = (callback: () => void, options?: { message?: string; title?: string }) => {
    setOnConfirm(() => callback);
    setMessage(options?.message ?? '');
    setTitle(options?.title ?? '');
    setOpen(true);
  };

  const closeDialog = () => {
    setOpen(false);
    setOnConfirm(null);
  };

  const contextValue = useMemo(() => ({ openDialog }), []);

  const { t: tCommon } = useTranslate('common');

  return (
    <ConfirmDialogContext.Provider value={contextValue}>
      {children}
      <Dialog
        open={open}
        onClose={closeDialog}
        sx={{ borderRadius: 2, '& .MuiPaper-root': { borderRadius: 4 } }}
      >
        <DialogTitle>{title || tCommon('dialog.title')}</DialogTitle>
        <DialogContent>{message || tCommon('dialog.message')}</DialogContent>
        <DialogActions sx={{ display: 'flex', alignItems: 'center', p: 3 }}>
          <Button variant="outlined" color="error" onClick={closeDialog} sx={{ borderRadius: 2 }}>
            {tCommon('cancel')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              onConfirm?.();
              closeDialog();
            }}
            sx={{ borderRadius: 2 }}
          >
            {tCommon('confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </ConfirmDialogContext.Provider>
  );
}

export function useConfirmDialog() {
  const context = useContext(ConfirmDialogContext);
  if (!context) {
    throw new Error('useConfirmDialog must be used within a ConfirmDialogProvider');
  }
  return context;
}
